// client/src/utils/calendarIntegration.js
import { parseISO } from 'date-fns'
import { formatDateTime } from './format'

/**
 * Generate match description text
 * @param {Object} match - Match object
 * @param {Object} options - Optional overrides for details
 * @returns {string} Formatted description
 */
const generateMatchDescription = (match, options = {}) => {
  const leagueName = options.leagueName || match.league_name || 'Unknown League'
  const opponentName = options.opponentName || getOpponentName(match)

  return `
Serve Sync Tennis ${leagueName} Match with ${opponentName}
`.trim()
}

/**
 * Generate Google Calendar URL for a match
 * @param {Object} match - Match object with datetime and opponent info
 * @param {Object} options - Optional details { leagueName, opponentName }
 * @returns {string} Google Calendar URL
 */
export const generateGoogleCalendarURL = (match, options = {}) => {
  if (!match.match_datetime) return ''

  const startDate = parseISO(match.match_datetime)
  // Assume 2-hour duration for tennis matches (can be made configurable)
  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000)

  const opponentName = options.opponentName || getOpponentName(match)
  const title = `Tennis Match vs ${opponentName}`
  const description = generateMatchDescription(match, options)

  const location = getMatchLocation(match)

  // Format dates for Google Calendar as true UTC (YYYYMMDDTHHMMSSZ)
  const startDateStr = toUTCDateTimeBasic(startDate)
  const endDateStr = toUTCDateTimeBasic(endDate)

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${startDateStr}/${endDateStr}`,
    details: description,
    location: location,
  })

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/**
 * Generate Outlook Calendar URL for a match
 * @param {Object} match - Match object with datetime and opponent info
 * @param {Object} options - Optional details { leagueName, opponentName }
 * @returns {string} Outlook Calendar URL
 */
export const generateOutlookCalendarURL = (match, options = {}) => {
  if (!match.match_datetime) return ''

  const startDate = parseISO(match.match_datetime)
  // Assume 2-hour duration for tennis matches
  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000)

  const opponentName = options.opponentName || getOpponentName(match)
  const title = `Tennis Match vs ${opponentName}`
  const description = generateMatchDescription(match, options)

  const location = getMatchLocation(match)

  // Format dates for Outlook (ISO 8601)
  const startDateStr = startDate.toISOString()
  const endDateStr = endDate.toISOString()

  const params = new URLSearchParams({
    subject: title,
    startdt: startDateStr,
    enddt: endDateStr,
    body: description,
    location: location,
  })

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`
}

/**
 * Generate ICS file content for a match
 * @param {Object} match - Match object with datetime and opponent info
 * @param {Object} options - Optional details { leagueName, opponentName }
 * @returns {string} ICS file content
 */
export const generateICSContent = (match, options = {}) => {
  if (!match.match_datetime) return ''

  const startDate = parseISO(match.match_datetime)
  // Assume 2-hour duration for tennis matches
  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000)

  const opponentName = options.opponentName || getOpponentName(match)
  const title = `Tennis Match vs ${opponentName}`
  const description = generateMatchDescription(match, options)

  const location = getMatchLocation(match)

  // Format dates for ICS as true UTC (YYYYMMDDTHHMMSSZ)
  const startDateStr = toUTCDateTimeBasic(startDate)
  const endDateStr = toUTCDateTimeBasic(endDate)

  // Generate unique ID
  const uid = `match-${match.id}-${match.league_id}@flex-league.com`

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Serve Sync Tennis//Match Calendar//EN
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${toUTCDateTimeBasic(new Date())}
DTSTART:${startDateStr}
DTEND:${endDateStr}
SUMMARY:${title}
DESCRIPTION:${escapeICSText(description)}
LOCATION:${escapeICSText(location)}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`

  return icsContent
}

/**
 * Generate ICS content for multiple matches
 * @param {Array} matches - Array of match objects
 * @returns {string} ICS file content for all matches
 */
export const generateBulkICSContent = (matches) => {
  if (!matches || matches.length === 0) return ''

  const events = matches
    .filter((match) => match.match_datetime) // Only include matches with valid datetime
    .map((match) => {
      const startDate = parseISO(match.match_datetime)
      const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000)

      const opponentName = getOpponentName(match)
      const title = `Tennis Match vs ${opponentName}`
      const description = generateMatchDescription(match, {}) // No external options for bulk yet

      const location = getMatchLocation(match)
      const uid = `match-${match.id}-${match.league_id}@flex-league.com`

      return `BEGIN:VEVENT
UID:${uid}
DTSTAMP:${toUTCDateTimeBasic(new Date())}
DTSTART:${toUTCDateTimeBasic(startDate)}
DTEND:${toUTCDateTimeBasic(endDate)}
SUMMARY:${title}
DESCRIPTION:${escapeICSText(description)}
LOCATION:${escapeICSText(location)}
STATUS:CONFIRMED
END:VEVENT`
    })
    .join('\n')

  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Serve Sync Tennis//Match Calendar//EN
${events}
END:VCALENDAR`
}

/**
 * Download ICS file
 * @param {string} icsContent - ICS file content
 * @param {string} filename - Filename for the download
 */
export const downloadICSFile = (icsContent, filename = 'matches.ics') => {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  window.URL.revokeObjectURL(url)
}

/**
 * Helper function to get opponent name from match data
 * @param {Object} match - Match object
 * @returns {string} Opponent name
 */
const getOpponentName = (match) => {
  if (!match.current_player_id) return 'Unknown Opponent'

  const isCurrentPlayerHome = match.home_player_id === match.current_player_id

  if (isCurrentPlayerHome) {
    return (
      `${match.away_first_name || ''} ${match.away_last_name || ''}`.trim() ||
      'Unknown Opponent'
    )
  } else {
    return (
      `${match.home_first_name || ''} ${match.home_last_name || ''}`.trim() ||
      'Unknown Opponent'
    )
  }
}

/**
 * Helper function to build match location from home court fields
 * @param {Object} match - Match object
 * @returns {string} Formatted location string
 */
const getMatchLocation = (match) => {
  // Build location from home_court_name, city, and state
  const parts = []

  if (match.home_court_name) {
    parts.push(match.home_court_name)
  }

  // Add address if available
  if (match.home_court_address) {
    parts.push(match.home_court_address)
  }

  // Add city and state on same line if available
  const cityState = [match.home_court_city, match.home_court_state]
    .filter(Boolean)
    .join(', ')

  if (cityState) {
    parts.push(cityState)
  }

  // Return formatted location or default
  return parts.length > 0 ? parts.join(', ') : 'TBD'
}

/**
 * Format match datetime for display in calendar integration
 * @param {string} datetime - ISO datetime string
 * @returns {string} Formatted datetime string
 */
export const formatMatchDateTime = (datetime) => {
  if (!datetime) return 'TBD'
  try {
    return formatDateTime(datetime)
  } catch {
    return 'Invalid Date'
  }
}

// Helpers
// Convert a Date to UTC basic format used by ICS/Google: YYYYMMDDTHHMMSSZ
const toUTCDateTimeBasic = (date) => {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  const hh = String(date.getUTCHours()).padStart(2, '0')
  const mm = String(date.getUTCMinutes()).padStart(2, '0')
  const ss = String(date.getUTCSeconds()).padStart(2, '0')
  return `${y}${m}${d}T${hh}${mm}${ss}Z`
}

// Escape text per RFC 5545 (commas, semicolons, backslashes, and newlines)
const escapeICSText = (text) => {
  return String(text || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
}
